"use strict";
/* tslint:disable */
/* eslint-disable */
/**
 * Federations API
 * API for initiating and accepting federation relationships.  Access to most of this API is gated by the `federations` domain right.  This API has a UI at `/admin/federations`.  The `view_data_federation_source_domain` domain right controls who can see the source domain of federated assets.  # Setting up a New Federation Relationship  There are two steps to setting up a new federation relationship between two domains.  ## Step 1: Initiate the federation from the source domain  The [\"Initiate an Outgoing Federation\"](#operation/initiateFederation) operation must first be called from the [Source Domain](#section/Glossary/Source-domain). The `targetDomainCName` parameter should be the target domain.  It is possible that a relationship between two domains is invalid; to check this before attempting to initiate the relationship, the [\"Validate a Federation Relationship\"](#operation/isValidFederation) operation can be used.  ## Step 2: Accept the federation on the target domain  On the target domain, the [\"Accept an Incoming Federation\"](#operation/acceptFederation) operation must be called with the ID of the federation relationship that was created in the call from the source domain.  To see pending federation relationships, the [\"List Current Domain Federations\"](#operation/getFederationsForCurrentDomain) operation can be used.  # Glossary  ## Source domain  This is the domain that assets are coming from.  Assets federate from the source domain out to the target domain.  ## Target domain  This is the domain that assets are going to.  Assets federate to the target domain from the source domain.  ## Incoming federation  Assets are federating into the current domain in the federation relationship.  ## Outgoing federation  Assets are federating out of the current domain in the federation relationship.  ## Catalog federation  Assets will show up on the target domain\'s catalog (i.e. `/browse`, `/admin/assets`, and others) and will link back to the source domain.  Only users who have permission to see the assets on the target domain can actually view the assets once they get there; that is, if the source domain is a locked down domain (i.e. the `restrict_community_access` feature flag or some form of [\"staging lockdown\"](https://socrata.atlassian.net/wiki/spaces/GS/pages/359597417/Staging+Lockdown+What+does+it+do+When+should+we+use+it) is on) then users won\'t be able to see assets from the source domain.  There are also multiple \"levels\" of catalog federation that are controlled by the `data_catalog_audience_level` feature flag.  If `data_catalog_audience_level` is...   - `internal` on the source _or_ target domain     - Only roled users can see the `/browse` page   - `internal` on the target domain     - Assets that are shared to the \"internal\" scope on the source domain (see the view permissions API) show up on the target domain\'s `/browse` page   - `open` on the target domain     - Only assets that are shared to the \"public\" scope on the source domain show up on the target domain.   - `off` on the source _or_ target domain     - There is no public catalog  ## Data federation  Assets try their best to show up as \"native\" assets on the target domain.  This means that they show up as basically normal assets to unroled users.  When a link to a federated asset is clicked, the user remains on the target domain.  There are some caveats: - Not all asset types will be federated (See also: [Decision: What assets are not supported in MBI 1 of Internal->Open Federation](https://socrata.atlassian.net/wiki/spaces/PD/pages/456786407/Decision+What+assets+are+not+supported+in+MBI+1+of+Internal-+Open+Federation)) - Assets can only be edited on the source domain  ## Federation type and scope combinations  Federations can be initiated with a specific `scope` (which assets will be federated) and `type` (whether it is a data or catalog federation).  Currently, valid `scope` and `type` combinations are as follows:  | `scope`        | `type`    | Description                                                                                  | |----------------|-----------|----------------------------------------------------------------------------------------------| | `by_audience`  | `catalog` | All public (and optionally all internal) assets are catalog-federated to the target domain.  | | `by_audience`  | `data`    | All public assets are data-federated to the target domain.                                   | | `asset`        | `data`    | A specific asset is being data-federated to the target domain.                               |  Note that `asset` federations must use the asset-specific federation APIs.  # Changelog  ## 1.3.3  Add deleteAllFederationsForDomain to Federation schema  ## 1.3.1  Rename scopes: asset -> by_asset & public -> by_audience  ## 1.3.0  Add asset-specific federation.  ## 1.2.2  Add `searchBoost` to Federation schema  ## 1.2.1  Add `scope` and `type` to Federation schema  ## 1.2.0  Add `scope` and `type`.  ## 1.1.1  Documentation updates.  ## 1.1.0  Add `isIncomingFederation` to `Federation` schema.  ## 1.0.1  Add [new endpoint](#operation/getAudienceLevelForProposedTarget) to determine audience level for proposed federation target domain.  ## 1.0.0  Initial specification.
 *
 * The version of the OpenAPI document: 1.3.3
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FederationsApi = void 0;
var runtime = require("../runtime");
var models_1 = require("../models");
/**
 *
 */
var FederationsApi = /** @class */ (function (_super) {
    __extends(FederationsApi, _super);
    function FederationsApi() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Accept an incoming federation from a target domain.  This always assumes that the target domain is the current domain. If an attempt is made to accept a federation from the target domain, an error will be returned.
     * Accept an Incoming Federation
     */
    FederationsApi.prototype.acceptFederationRaw = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var queryParameters, headerParameters, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (requestParameters.federationId === null || requestParameters.federationId === undefined) {
                            throw new runtime.RequiredError('federationId', 'Required parameter requestParameters.federationId was null or undefined when calling acceptFederation.');
                        }
                        queryParameters = {};
                        headerParameters = {};
                        return [4 /*yield*/, this.request({
                                path: "/api/federations/{federationId}/accept".replace("{" + "federationId" + "}", encodeURIComponent(String(requestParameters.federationId))),
                                method: 'PUT',
                                headers: headerParameters,
                                query: queryParameters,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, new runtime.JSONApiResponse(response, function (jsonValue) { return models_1.FederationFromJSON(jsonValue); })];
                }
            });
        });
    };
    /**
     * Accept an incoming federation from a target domain.  This always assumes that the target domain is the current domain. If an attempt is made to accept a federation from the target domain, an error will be returned.
     * Accept an Incoming Federation
     */
    FederationsApi.prototype.acceptFederation = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.acceptFederationRaw(requestParameters)];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.value()];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Delete all federation relationships on a domain
     * Delete all the federations on a domain
     */
    FederationsApi.prototype.deleteAllFederationsForDomainRaw = function () {
        return __awaiter(this, void 0, void 0, function () {
            var queryParameters, headerParameters, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        queryParameters = {};
                        headerParameters = {};
                        return [4 /*yield*/, this.request({
                                path: "/api/federations/deleteAllFederationsForDomain",
                                method: 'DELETE',
                                headers: headerParameters,
                                query: queryParameters,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, new runtime.VoidApiResponse(response)];
                }
            });
        });
    };
    /**
     * Delete all federation relationships on a domain
     * Delete all the federations on a domain
     */
    FederationsApi.prototype.deleteAllFederationsForDomain = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.deleteAllFederationsForDomainRaw()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Delete a federation relationship
     * Delete a Federation
     */
    FederationsApi.prototype.deleteFederationRaw = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var queryParameters, headerParameters, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (requestParameters.federationId === null || requestParameters.federationId === undefined) {
                            throw new runtime.RequiredError('federationId', 'Required parameter requestParameters.federationId was null or undefined when calling deleteFederation.');
                        }
                        queryParameters = {};
                        headerParameters = {};
                        return [4 /*yield*/, this.request({
                                path: "/api/federations/{federationId}".replace("{" + "federationId" + "}", encodeURIComponent(String(requestParameters.federationId))),
                                method: 'DELETE',
                                headers: headerParameters,
                                query: queryParameters,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, new runtime.VoidApiResponse(response)];
                }
            });
        });
    };
    /**
     * Delete a federation relationship
     * Delete a Federation
     */
    FederationsApi.prototype.deleteFederation = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.deleteFederationRaw(requestParameters)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Determine the audience level of assets that will be involved in a proposed federation.  See response schema for more details.
     * Get Federation Audience Level
     */
    FederationsApi.prototype.getAudienceLevelForProposedTargetRaw = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var queryParameters, headerParameters, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (requestParameters.targetDomainCName === null || requestParameters.targetDomainCName === undefined) {
                            throw new runtime.RequiredError('targetDomainCName', 'Required parameter requestParameters.targetDomainCName was null or undefined when calling getAudienceLevelForProposedTarget.');
                        }
                        queryParameters = {};
                        if (requestParameters.targetDomainCName !== undefined) {
                            queryParameters['targetDomainCName'] = requestParameters.targetDomainCName;
                        }
                        headerParameters = {};
                        return [4 /*yield*/, this.request({
                                path: "/api/federations/audienceLevelForProposedTarget",
                                method: 'GET',
                                headers: headerParameters,
                                query: queryParameters,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, new runtime.JSONApiResponse(response, function (jsonValue) { return models_1.AudienceLevelFromJSON(jsonValue); })];
                }
            });
        });
    };
    /**
     * Determine the audience level of assets that will be involved in a proposed federation.  See response schema for more details.
     * Get Federation Audience Level
     */
    FederationsApi.prototype.getAudienceLevelForProposedTarget = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getAudienceLevelForProposedTargetRaw(requestParameters)];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.value()];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Get a list of all federation relationships for the current domain
     * List Current Domain Federations
     */
    FederationsApi.prototype.getFederationsForCurrentDomainRaw = function () {
        return __awaiter(this, void 0, void 0, function () {
            var queryParameters, headerParameters, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        queryParameters = {};
                        headerParameters = {};
                        return [4 /*yield*/, this.request({
                                path: "/api/federations",
                                method: 'GET',
                                headers: headerParameters,
                                query: queryParameters,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, new runtime.JSONApiResponse(response, function (jsonValue) { return jsonValue.map(models_1.FederationFromJSON); })];
                }
            });
        });
    };
    /**
     * Get a list of all federation relationships for the current domain
     * List Current Domain Federations
     */
    FederationsApi.prototype.getFederationsForCurrentDomain = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getFederationsForCurrentDomainRaw()];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.value()];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Initiate a federation between the current domain and a target domain.  This always assumes that the source domain is the current domain.
     * Initiate an Outgoing Federation
     */
    FederationsApi.prototype.initiateFederationRaw = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var queryParameters, headerParameters, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (requestParameters.targetDomainCName === null || requestParameters.targetDomainCName === undefined) {
                            throw new runtime.RequiredError('targetDomainCName', 'Required parameter requestParameters.targetDomainCName was null or undefined when calling initiateFederation.');
                        }
                        queryParameters = {};
                        if (requestParameters.targetDomainCName !== undefined) {
                            queryParameters['targetDomainCName'] = requestParameters.targetDomainCName;
                        }
                        if (requestParameters.type !== undefined) {
                            queryParameters['type'] = requestParameters.type;
                        }
                        if (requestParameters.scope !== undefined) {
                            queryParameters['scope'] = requestParameters.scope;
                        }
                        if (requestParameters.searchBoost !== undefined) {
                            queryParameters['searchBoost'] = requestParameters.searchBoost;
                        }
                        headerParameters = {};
                        return [4 /*yield*/, this.request({
                                path: "/api/federations/initiate",
                                method: 'POST',
                                headers: headerParameters,
                                query: queryParameters,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, new runtime.JSONApiResponse(response, function (jsonValue) { return models_1.FederationFromJSON(jsonValue); })];
                }
            });
        });
    };
    /**
     * Initiate a federation between the current domain and a target domain.  This always assumes that the source domain is the current domain.
     * Initiate an Outgoing Federation
     */
    FederationsApi.prototype.initiateFederation = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.initiateFederationRaw(requestParameters)];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.value()];
                    case 2: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Determine whether a given federation relationship between two domains is valid or not.  This always assumes that the source domain is the current domain.
     * Validate a Federation Relationship
     */
    FederationsApi.prototype.isValidFederationRaw = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var queryParameters, headerParameters, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (requestParameters.targetDomainCName === null || requestParameters.targetDomainCName === undefined) {
                            throw new runtime.RequiredError('targetDomainCName', 'Required parameter requestParameters.targetDomainCName was null or undefined when calling isValidFederation.');
                        }
                        queryParameters = {};
                        if (requestParameters.targetDomainCName !== undefined) {
                            queryParameters['targetDomainCName'] = requestParameters.targetDomainCName;
                        }
                        if (requestParameters.type !== undefined) {
                            queryParameters['type'] = requestParameters.type;
                        }
                        if (requestParameters.scope !== undefined) {
                            queryParameters['scope'] = requestParameters.scope;
                        }
                        headerParameters = {};
                        return [4 /*yield*/, this.request({
                                path: "/api/federations/validFederation",
                                method: 'GET',
                                headers: headerParameters,
                                query: queryParameters,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, new runtime.VoidApiResponse(response)];
                }
            });
        });
    };
    /**
     * Determine whether a given federation relationship between two domains is valid or not.  This always assumes that the source domain is the current domain.
     * Validate a Federation Relationship
     */
    FederationsApi.prototype.isValidFederation = function (requestParameters) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.isValidFederationRaw(requestParameters)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    return FederationsApi;
}(runtime.BaseAPI));
exports.FederationsApi = FederationsApi;
